var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { TIMEZONES, USA_STATE_TO_TIMEZONE } from "@tellescope/types-models";
import { ADMIN_ROLE, get_inverse_relationship_type } from "@tellescope/constants";
import sanitizeHtml from 'sanitize-html';
import { DateTime } from "luxon";
import { ObjectId } from "./ObjectId/objectid";
export { ObjectId };
export var user_is_admin = function (u) { var _a; return u.type === 'enduser' ? false : !!((_a = u === null || u === void 0 ? void 0 : u.roles) === null || _a === void 0 ? void 0 : _a.includes(ADMIN_ROLE)); };
export var first_letter_capitalized = function (s) {
    if (s === void 0) { s = ''; }
    return s.charAt(0).toUpperCase() + s.slice(1);
};
export var first_letter_lowercase = function (s) {
    if (s === void 0) { s = ''; }
    return s.charAt(0).toLowerCase() + s.slice(1);
};
export var object_is_empty = function (o) { return Object.keys(o).length === 0 && o.constructor === Object; };
export var is_truthy = function (f) { return !!f; };
export var is_defined = function (f) { return f !== undefined; };
export var filter_object = function (o, validator) {
    if (o === void 0) { o = {}; }
    if (validator === void 0) { validator = is_defined; }
    var newObject = {};
    for (var f in o) {
        var value = o[f];
        if (validator(value))
            newObject[f] = value;
    }
    return newObject;
};
export var is_object = function (obj) { return typeof obj === "object" && obj !== null; };
// fields that are defined and match by equality
export var matching_fields = function (fields, o1, o2) {
    var matches = {};
    for (var _i = 0, fields_1 = fields; _i < fields_1.length; _i++) {
        var k = fields_1[_i];
        if (o1[k] && o1[k] === o2[k]) { // may need deeper equality check for objects
            matches[k] = o1[k];
        }
    }
    return matches;
};
var WHITE_SPACE_EXP = /^\s*$/;
export var is_whitespace = function (s) {
    if (s === void 0) { s = ''; }
    return WHITE_SPACE_EXP.test(s);
};
export var url_safe_path = function (p) {
    if (p === void 0) { p = ''; }
    return p.replace(/_/g, '-');
};
export var to_object_id = function (s) {
    if (s === void 0) { s = ''; }
    return new ObjectId(s);
};
export var objects_equivalent = function (o1, o2) {
    if (o1 === null || o2 === null)
        return o1 === o2; // null is base case for typeof === object
    // date case
    if (o1 instanceof Date && typeof o2 === 'string') {
        return o1.getTime() === new Date(o2).getTime();
    }
    if (o2 instanceof Date && typeof o1 === 'string') {
        return o2.getTime() === new Date(o1).getTime();
    }
    if (typeof o1 !== "object" || typeof o2 !== 'object')
        return o1 === o2; // base case  
    var k1 = Object.keys(o1), k2 = Object.keys(o2);
    for (var _i = 0, k1_1 = k1; _i < k1_1.length; _i++) { // keys must be equal sets
        var k = k1_1[_i];
        if (!k2.includes(k))
            return false;
    }
    for (var _a = 0, k2_1 = k2; _a < k2_1.length; _a++) { // keys must be equal sets
        var k = k2_1[_a];
        if (!k1.includes(k))
            return false;
    }
    for (var _b = 0, k1_2 = k1; _b < k1_2.length; _b++) { // recurse in case 
        var k = k1_2[_b];
        if (!objects_equivalent(o1[k], o2[k])) {
            return false;
        }
    }
    return true;
};
export var user_display_name = function (user) {
    if (!user)
        return '';
    var fname = user.fname, lname = user.lname, email = user.email, phone = user.phone, id = user.id, displayName = user.displayName, internalDisplayName = user.internalDisplayName;
    if (internalDisplayName)
        return internalDisplayName;
    if (fname && lname)
        return "".concat(fname, " ").concat(lname);
    if (fname)
        return fname;
    if (displayName)
        return displayName;
    if (email)
        return email;
    if (phone)
        return phone;
    if (id)
        return "User ".concat(id);
    return '';
};
export var user_is_active = function (user, options) {
    if (!user)
        return null;
    var activeThresholdMS = (options === null || options === void 0 ? void 0 : options.activeThresholdMS) || 300000; // 5 minutes
    var inactiveThresholdMS = (options === null || options === void 0 ? void 0 : options.inactiveThresholdMS) || 1500000; // 15 minutes
    if (activeThresholdMS < 0)
        throw new Error('activeThresholdMS must be positive');
    if (inactiveThresholdMS < 0)
        throw new Error('inactiveThresholdMS must be positive');
    var lastLogout = new Date(user.lastLogout).getTime();
    var lastActive = new Date(user.lastActive).getTime();
    if (lastLogout > lastActive)
        return 'Unavailable';
    if (Date.now() - lastActive < activeThresholdMS)
        return 'Active';
    if (Date.now() - lastActive < inactiveThresholdMS)
        return 'Away';
    return 'Unavailable';
};
export var defined_fields = function (o) {
    var filtered = {};
    for (var field in o) {
        if (o[field] !== undefined)
            filtered[field] = o[field];
    }
    return filtered;
};
export var truncate_string = function (s, options) {
    var _a, _b;
    if (s === void 0) { s = ''; }
    if (options === void 0) { options = {}; }
    if (typeof options.length === 'number' && options.length < 0)
        throw new Error("Length must be positive");
    var showEllipsis = (_a = options.showEllipsis) !== null && _a !== void 0 ? _a : true;
    var length = (_b = options.length) !== null && _b !== void 0 ? _b : 25;
    return (s.substring(0, length) +
        (showEllipsis && s.length > length ? '...' : ''));
};
export var map_object = function (object, handler) { return (Object.keys(object).map(function (key) { return handler(key, object[key]); })); };
var _a = [0, 1, 2, 3], LINK_START = _a[0], LINK_END = _a[1], TEXT_START = _a[2], TEXT_END = _a[3];
export var parse_link_template = function (text, startFrom) {
    var start = 0;
    var state = LINK_START;
    var linkChars = [];
    var linkTextChars = [];
    for (var i = startFrom || 0; i < text.length; i++) {
        var char = text[i];
        if (state === LINK_START) {
            if (char === '{') {
                start = i;
                state = LINK_END;
            }
        }
        else if (state === LINK_END) {
            if (char === '}') {
                state = TEXT_START;
            }
            else {
                linkChars.push(char);
            }
        }
        else if (state === TEXT_START) {
            if (char === '[') {
                state = TEXT_END;
            }
            else if (!is_whitespace(char)) { // only allow whitespace between {link} and [linkText]
                start = 0;
                linkChars = [];
                state = LINK_START; // start seeking new link
            }
        }
        else {
            if (char === ']') {
                return {
                    url: linkChars.join(''),
                    displayName: linkTextChars.join('')
                };
            }
            else {
                linkTextChars.push(char);
            }
        }
    }
    return null;
};
var findTemplateElement = function (text) {
    var startIndex = text.indexOf('{{');
    if (startIndex === -1)
        return undefined;
    var endIndex = text.indexOf('}}');
    if (endIndex === -1)
        return undefined;
    // omits start '{{' with +2, endIndex is not inclusive, so it omits last '}}' 
    return text.substring(startIndex + 2, endIndex);
};
export var getTemplatedData = function (text) {
    var value = findTemplateElement(text);
    var badValue = function (reason) {
        if (reason === void 0) { reason = ''; }
        throw Error("Unrecognized template field: ".concat(value).concat(reason && " (".concat(reason, ")")));
    };
    if (value === undefined)
        return badValue();
    if (value.startsWith('forms') || value.startsWith('form_groups')) {
        var _a = value.split('.'), _1 = _a[0], formId = _a[1], _field = _a[2], rest = _a.slice(3);
        var field = _field + (rest.length
            ? ('.' + rest.join('.'))
            : '');
        if (field.startsWith('link')) {
            return { id: formId, displayName: field.substring(5) };
        }
        return badValue();
    }
    if (value.startsWith('files')) {
        var _b = value.split('.'), _2 = _b[0], fileId = _b[1], field = _b[2];
        if (field.startsWith('link')) {
            return { id: fileId, displayName: field.substring(5) };
        }
        throw Error("Unrecognized template field: ".concat(value));
    }
    if (value.startsWith('content')) {
        var _d = value.split('.'), _3 = _d[0], fileId = _d[1], field = _d[2];
        if (field.startsWith('link')) {
            return { id: fileId, displayName: field.substring(5) };
        }
        throw Error("Unrecognized template field: ".concat(value));
    }
    if (value.startsWith('portal.')) {
        var _e = value.split('.'), _4 = _e[0], action = _e[1], pageWithText = _e[2];
        if (action.startsWith('link') && pageWithText) {
            var _f = pageWithText.split(':').map(function (s) { return s.trim(); }), page = _f[0], displayName = _f[1];
            if (page && displayName) {
                return { page: page, displayName: displayName };
            }
        }
        throw Error("Unrecognized template field: ".concat(value));
    }
    return badValue();
};
export var build_link_string = function (d) { return "{".concat(d.url, "}[").concat(d.displayName, "]"); };
export var build_form_link_string = function (d) { return "{{forms.".concat(d.id, ".link:").concat(d.displayName, "}}"); };
export var build_form_group_link_string = function (d) { return "{{form_groups.".concat(d.id, ".link:").concat(d.displayName, "}}"); };
export var build_file_link_string = function (d) { return "{{files.".concat(d.id, ".link:").concat(d.displayName, "}}"); };
export var build_content_link_string = function (d) { return "{{content.".concat(d.id, ".link:").concat(d.displayName, "}}"); };
export var build_portal_link_string = function (d) { return "{{portal.link.".concat(d.page, ":").concat(d.displayName, "}}"); };
export var to_absolute_url = function (link) { return link.startsWith('http') ? link : '//' + link; }; // ensure absolute url 
export var throwFunction = function (s) { throw s; };
export var wait = function (f, ms) {
    if (ms === void 0) { ms = 1000; }
    return new Promise(function (resolve, reject) {
        setTimeout(function () { return f ? f.then(resolve).catch(reject) : resolve(); }, ms);
    });
};
export var sorted_records = function (records, options) {
    return __spreadArray([], records, true).sort(function (_r1, _r2) {
        var _a, _b;
        var r1 = (options === null || options === void 0 ? void 0 : options.direction) === 'oldFirst' ? _r2 : _r1;
        var r2 = (options === null || options === void 0 ? void 0 : options.direction) === 'oldFirst' ? _r1 : _r2;
        var result = (new Date(r1[(_a = options === null || options === void 0 ? void 0 : options.key) !== null && _a !== void 0 ? _a : 'createdAt']).getTime()
            - new Date(r2[(_b = options === null || options === void 0 ? void 0 : options.key) !== null && _b !== void 0 ? _b : 'createdAt']).getTime());
        if ((options === null || options === void 0 ? void 0 : options.reverseIfEqual) && result === 0)
            return -1;
        return result;
    });
};
export var MONTHS_FULL = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];
export var MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];
export var get_time_values = function (date, options) {
    var dayOfMonth = date.getDate();
    var monthNumber = date.getMonth() + 1;
    var month = ((options === null || options === void 0 ? void 0 : options.fullMonth) ? MONTHS_FULL : MONTHS)[monthNumber - 1];
    var hours = date.getHours();
    var minutesRaw = date.getMinutes();
    var minutes = minutesRaw >= 10 ? minutesRaw : "0".concat(minutesRaw);
    var year = date.getFullYear();
    var amPm = hours < 12 ? 'am' : 'pm';
    var hoursAmPm = (hours === 0
        ? 12
        : hours <= 12 ? hours : hours - 12);
    return { dayOfMonth: dayOfMonth, monthNumber: monthNumber, month: month, hours: hours, hoursAmPm: hoursAmPm, amPm: amPm, minutes: minutes, year: year, };
};
export var formatted_date = function (date) {
    var _a = get_time_values(date), dayOfMonth = _a.dayOfMonth, month = _a.month, year = _a.year, hoursAmPm = _a.hoursAmPm, amPm = _a.amPm, minutes = _a.minutes;
    if (isNaN(dayOfMonth)
        || !month
        || isNaN(year)
        || isNaN(hoursAmPm)
        || !amPm
        || (typeof minutes === 'number' && isNaN(minutes))) {
        return '';
    }
    return "".concat(month, " ").concat(dayOfMonth, " ").concat(year, ", ").concat(hoursAmPm, ":").concat(minutes).concat(amPm);
};
export var get_add_to_gcal_link = function (event) {
    var start = DateTime.fromMillis(event.startTimeInMS, { zone: 'UTC' });
    var end = DateTime.fromMillis(event.startTimeInMS + event.durationInMinutes * 60 * 1000, { zone: 'UTC' });
    var startString = "".concat(start.toFormat('yyyyLLdd'), "T").concat(start.toFormat('HHmmss'), "Z");
    var endString = "".concat(end.toFormat('yyyyLLdd'), "T").concat(end.toFormat('HHmmss'), "Z");
    return ("https://calendar.google.com/calendar/render?action=TEMPLATE&text=".concat(event.title.replaceAll(' ', '+'), "&details=").concat(event.videoURL || '', "&dates=").concat(startString, "/").concat(endString));
};
export var formatted_date_hh_mm = function (date) {
    var _a = get_time_values(date), minutes = _a.minutes, hoursAmPm = _a.hoursAmPm, amPm = _a.amPm;
    return "".concat(hoursAmPm, ":").concat(minutes).concat(amPm);
};
/** @deprecated uses month name instead of number */
export var yyyy_mm_dd = function (date) {
    var _a = get_time_values(date), dayOfMonth = _a.dayOfMonth, month = _a.month, year = _a.year;
    return "".concat(year, "-").concat(month, "-").concat(dayOfMonth);
};
export var yyyy_mm_dd_numeric = function (date) {
    var _a = get_time_values(date), dayOfMonth = _a.dayOfMonth, monthNumber = _a.monthNumber, year = _a.year;
    return "".concat(year, "-").concat(monthNumber, "-").concat(dayOfMonth);
};
export var mm_dd_yyyy = function (date) {
    var _a = get_time_values(date), dayOfMonth = _a.dayOfMonth, monthNumber = _a.monthNumber, year = _a.year;
    return "".concat(monthNumber < 10 ? '0' : '').concat(monthNumber, "-").concat(dayOfMonth < 10 ? '0' : '').concat(dayOfMonth, "-").concat(year);
};
export var fullMonth_day_year = function (date) {
    var _a = get_time_values(date, { fullMonth: true }), dayOfMonth = _a.dayOfMonth, month = _a.month, year = _a.year;
    return "".concat(month, " ").concat(dayOfMonth, ", ").concat(year);
};
export var time_for_calendar_event = function (event) {
    var start = new Date(event.startTimeInMS);
    var end = new Date(event.startTimeInMS + event.durationInMinutes * 60 * 1000);
    var _a = get_time_values(start), hoursAmPm = _a.hoursAmPm, minutes = _a.minutes, amPm = _a.amPm;
    var _b = get_time_values(end), hoursEnd = _b.hoursAmPm, minutesEnd = _b.minutes, amPmEnd = _b.amPm;
    return "".concat(hoursAmPm, ":").concat(minutes).concat(amPm === amPmEnd ? '' : amPm, "-").concat(hoursEnd, ":").concat(minutesEnd).concat(amPmEnd);
};
export var remove_script_tags = function (s) { return s.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ''); };
export var remove_style_tags = function (s) { return s.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ''); };
export var remove_image_tags = function (s) { return s.replace(/<img[\s\S]*?>/gi, ''); };
export var query_string_for_object = function (query) {
    var queryString = '';
    if (query && !object_is_empty(query)) {
        queryString = '?';
        for (var key in query) {
            queryString += "".concat(key, "=").concat(query[key], "&");
        }
    }
    return queryString;
};
export var PROD_API_URL = 'https://api.tellescope.com';
export var STAGING_API_URL = 'https://staging-api.tellescope.com';
export var TEST_API_URL = "http://localhost:8080";
export var getEnvironment = function () { return (window.location.origin.includes('staging')
    ? 'staging'
    : (window.location.origin.includes('localhost:') || window.location.origin.includes('127.0.0.1:')) // don't check for Tellescope, may be hosted on a custom URL
        ? 'test'
        : 'prod'); };
export var getApiURL = function () { return (window.location.origin.includes('staging')
    ? STAGING_API_URL
    : (window.location.origin.includes('localhost:') || window.location.origin.includes('127.0.0.1:')) // don't check for Tellescope, may be hosted on a custom URL
        ? TEST_API_URL
        : PROD_API_URL); };
export var getGoogleClientId = function () {
    var api = getApiURL();
    return (api === PROD_API_URL
        ? "526353775713-puib79782ac254evqj2fs0sb7acsij65.apps.googleusercontent.com"
        : api === STAGING_API_URL
            ? '826276796073-6rcrqp4duqatbhn8k71saopenqc2fb0i.apps.googleusercontent.com'
            : '842986734352-2c73i6iq2aoau3dj1jvqtmufn2j9g0dm.apps.googleusercontent.com');
};
export var getGoogleClientAPIKey = function () {
    var api = getApiURL();
    return (api === PROD_API_URL
        ? 'AIzaSyCYCWiLkYifffepu5L-p1x4yUExf5aWpuA'
        : api === STAGING_API_URL
            ? "AIzaSyDUCT6r5qQx8yniMuP-Y9Ni08THoLQtG6M"
            : "AIzaSyBW9D0mg3ISvNQcSB0Z_PwHNuD0OhFdMKg");
};
var PUBLIC_ASSET_BUCKET = "tellescope-public-files";
export var getBuiltInPublicFileName = function (_a) {
    var _b;
    var name = _a.name, organizationIds = _a.organizationIds;
    return ("".concat(name).concat((_b = organizationIds === null || organizationIds === void 0 ? void 0 : organizationIds[organizationIds.length - 1]) !== null && _b !== void 0 ? _b : ''));
};
export var getOrgnizationLogoURL = function (_a) {
    var organizationIds = _a.organizationIds, id = _a.id, businessId = _a.businessId, logoVersion = _a.logoVersion, args = __rest(_a, ["organizationIds", "id", "businessId", "logoVersion"]);
    return (getPublicFileURL(__assign(__assign({}, args), { version: logoVersion, businessId: businessId !== null && businessId !== void 0 ? businessId : id, name: getBuiltInPublicFileName({ organizationIds: organizationIds, name: 'logo' }) })));
};
export var getOrgnizationFaviconURL = function (_a) {
    var organizationIds = _a.organizationIds, businessId = _a.businessId, id = _a.id, faviconVersion = _a.faviconVersion, args = __rest(_a, ["organizationIds", "businessId", "id", "faviconVersion"]);
    return (getPublicFileURL(__assign(__assign({}, args), { version: faviconVersion, businessId: businessId !== null && businessId !== void 0 ? businessId : id, name: getBuiltInPublicFileName({ organizationIds: organizationIds, name: 'favicon' }) })));
};
export var getPublicFileURL = function (_a) {
    var businessId = _a.businessId, name = _a.name, version = _a.version, apiURL = _a.apiURL;
    var api = apiURL || getApiURL();
    var ENV_PREFIX = api === PROD_API_URL ? "prod"
        : api === STAGING_API_URL ? "staging"
            : "test";
    return "https://".concat(PUBLIC_ASSET_BUCKET, ".s3.amazonaws.com/").concat(ENV_PREFIX, "/").concat(businessId, "/").concat(name, "?version=").concat(version !== null && version !== void 0 ? version : 0);
};
export var getDefaultPortalURL = function (_a) {
    var subdomain = _a.subdomain;
    var api = getApiURL();
    if (api === TEST_API_URL)
        return "http://localhost:3030";
    return ("https://".concat(subdomain, ".").concat(api === PROD_API_URL ? 'portal' : 'staging-portal', ".tellescope.com"));
};
export var matches_organization = function (value, orgInfo) {
    var _a, _b, _d, _e;
    // case of organization model itself where businessId isn't necessarily set
    if (value.id === (orgInfo === null || orgInfo === void 0 ? void 0 : orgInfo.businessId) && !(orgInfo === null || orgInfo === void 0 ? void 0 : orgInfo.organizationIds))
        return true;
    // case of using organization as orgInfo
    if (!(orgInfo === null || orgInfo === void 0 ? void 0 : orgInfo.businessId) && !((_a = value.organizationIds) === null || _a === void 0 ? void 0 : _a.length))
        return true;
    if ((value === null || value === void 0 ? void 0 : value.businessId) !== (orgInfo === null || orgInfo === void 0 ? void 0 : orgInfo.businessId))
        return false;
    if (((_b = value === null || value === void 0 ? void 0 : value.organizationIds) !== null && _b !== void 0 ? _b : []).length !== ((_d = orgInfo.organizationIds) !== null && _d !== void 0 ? _d : []).length)
        return false;
    // since length is same, we need a 1-way match that all organizationIds in orgInfo are found in value.organizationIds
    var mismatch = (_e = orgInfo.organizationIds) === null || _e === void 0 ? void 0 : _e.find(function (id) { var _a, _b; return !((_b = ((_a = value === null || value === void 0 ? void 0 : value.organizationIds) !== null && _a !== void 0 ? _a : [])) === null || _b === void 0 ? void 0 : _b.includes(id)); });
    if (mismatch)
        return false;
    return true;
};
export var is_suborganization = function (_a) {
    var parent = _a.parent, child = _a.child;
    for (var _i = 0, parent_1 = parent; _i < parent_1.length; _i++) {
        var value = parent_1[_i];
        if (!child.includes(value))
            return false;
    }
    return true;
};
// !u.organizationIds?.length // [] or undefined  
// || ( // enduser is shared with the user's organization
//   (enduser.sharedWithOrganizations ?? [])
//   .find(sharedIds => 
//     u.organizationIds?.find(ids => objects_equivalent(sharedIds, ids))
//   )
export var user_has_record_access = function (user, record) {
    var _a, _b, _d;
    if (user.businessId !== record.businessId)
        return false;
    if (!((_a = user.organizationIds) === null || _a === void 0 ? void 0 : _a.length))
        return true; // user is part of root organization
    if (record.organizationIds) {
        if (is_suborganization({ parent: (_b = user.organizationIds) !== null && _b !== void 0 ? _b : [], child: record.organizationIds })) {
            return true;
        }
    }
    for (var _i = 0, _e = (_d = record.sharedWithOrganizations) !== null && _d !== void 0 ? _d : []; _i < _e.length; _i++) {
        var organizationIds = _e[_i];
        // this must be an exact match
        if (objects_equivalent(organizationIds, user.organizationIds))
            return true;
    }
    return false;
};
export var is_table_input_response = function (v) { return (!!(Array.isArray(v) && Array.isArray(v[0]) && v[0][0].label)); };
export var form_response_value_to_string = function (value, options) {
    var _a, _b, _d, _e;
    if (value === null || value === undefined)
        return '';
    var maybeDate = (((options === null || options === void 0 ? void 0 : options.convertISODate) && typeof value === 'string')
        ? is_full_iso_string_heuristic(value)
        : undefined);
    if (maybeDate) {
        return formatted_date(maybeDate);
    }
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number' || typeof value === 'boolean')
        return value.toString();
    var anyValue = value;
    if ((anyValue === null || anyValue === void 0 ? void 0 : anyValue.value) !== 'object' && (anyValue === null || anyValue === void 0 ? void 0 : anyValue.unit)
        && typeof (anyValue === null || anyValue === void 0 ? void 0 : anyValue.value) !== 'object' && typeof (anyValue === null || anyValue === void 0 ? void 0 : anyValue.unit) !== 'object') {
        return "".concat(anyValue.value, " ").concat(anyValue.unit);
    }
    if ((anyValue === null || anyValue === void 0 ? void 0 : anyValue.text) && (anyValue === null || anyValue === void 0 ? void 0 : anyValue.recordId))
        return anyValue.text;
    if (anyValue.name) { // file
        return anyValue.name;
    }
    else if (Array.isArray(anyValue)) {
        // VALUE MAY BE UNDEFINED
        var value_1 = anyValue[0];
        if ((value_1 === null || value_1 === void 0 ? void 0 : value_1.text) && (value_1 === null || value_1 === void 0 ? void 0 : value_1.recordId)) { // DatabaseSelect repsonse
            return (anyValue.map(function (row) { return (row.text || 'No response provided'); }).join(', '));
        }
        if (Array.isArray(anyValue) && (((_a = anyValue[0]) === null || _a === void 0 ? void 0 : _a.displayTerm) || ((_b = anyValue[0]) === null || _b === void 0 ? void 0 : _b.drugName))) {
            return anyValue.map(function (medication) {
                var _a, _b, _d;
                if (medication === void 0) { medication = {}; }
                return "".concat(medication.drugName && medication.drugName !== 'Unknown' ? medication.drugName : medication.displayTerm, "\n").concat(((_a = medication.dosage) === null || _a === void 0 ? void 0 : _a.quantity) ? "".concat(medication.dosage.quantity, " units") : '').concat(((_b = medication.dosage) === null || _b === void 0 ? void 0 : _b.frequency) ? "".concat(((_d = medication.dosage) === null || _d === void 0 ? void 0 : _d.quantity) ? ', ' : '').concat(medication.dosage.frequency, "x daily") : '', "\nReason: ").concat(medication.reasonForTaking || 'Not provided');
            }).join('\n\n');
        }
        if (Array.isArray(value_1) && value_1[0].label) { // is Table Input response --> todo: replace with is_table_input_response
            return (anyValue
                .map(function (row) {
                return row
                    .map(function (c) { return c.entry || 'No response provided'; })
                    .join(', ');
            })
                .join('\n'));
        }
        return anyValue.join(', ');
    }
    else if (anyValue.fullName) { // signature
        return anyValue.fullName;
    }
    if (typeof anyValue === 'object') {
        var response = "\n";
        for (var k in anyValue) {
            response += "".concat(k, ": ").concat((_d = anyValue[k]) === null || _d === void 0 ? void 0 : _d.toString(), "\n");
        }
        return response;
        // return JSON.stringify(anyValue, null, 2)
    }
    return (_e = value === null || value === void 0 ? void 0 : value.toString()) !== null && _e !== void 0 ? _e : '';
};
export var is_organization_owner = function (organization, userId) { return (organization.owner // when owner is defined, creator is not relevant / overridden
    ? organization.owner === userId
    : organization.creator === userId); };
export var update_local_storage = function (key, value) {
    try {
        window.localStorage[key] = value;
    }
    catch (err) { }
};
export var read_local_storage = function (key) {
    try {
        return window.localStorage[key];
    }
    catch (err) { }
};
export var payment_cost_to_string = function (c) {
    if (c.currency === 'USD') {
        return "$".concat((c.amount / 100).toFixed(2));
    }
    return '';
};
export var safeJSONParse = function (s) {
    try {
        return JSON.parse(s);
    }
    catch (err) {
        return undefined;
    }
};
export var timezone_for_enduser = function (e) { return (e.timezone
    ? e.timezone
    : (e.state
        ? USA_STATE_TO_TIMEZONE[e.state]
        : undefined)); };
export var sanitize_html = function (t) { return sanitizeHtml(t, { allowedTags: [], allowedAttributes: {} }); };
export var plaintext_for_managed_content_record = function (record) {
    var _a;
    if (record.type === 'PDF')
        return null;
    if (record.type === 'Video')
        return null;
    if (!((_a = record.blocks) === null || _a === void 0 ? void 0 : _a.length))
        return null;
    return (record
        .blocks
        .filter(function (b) { return (b.type === 'h1'
        || b.type === 'h2'
        || b.type === 'html'); })
        .map(function (b) { return (b.type === 'h1'
        ? b.info.text
        : b.type === 'h2'
            ? b.info.text
            : b.type === 'html'
                ? sanitize_html(b.info.html)
                : ''); })
        .join('\n'));
};
// https://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
export var shuffle_array_in_place = function (array) {
    var _a;
    for (var i = array.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        _a = [array[j], array[i]], array[i] = _a[0], array[j] = _a[1];
    }
};
export var evaluate_conditional_logic = function (conditions, evaluate) {
    var key = Object.keys(conditions)[0]; // string is form id
    if (key === '$and') {
        var andConditions = conditions[key];
        for (var _i = 0, andConditions_1 = andConditions; _i < andConditions_1.length; _i++) {
            var c = andConditions_1[_i];
            if (!evaluate_conditional_logic(c, evaluate)) {
                return false;
            }
        }
        return true;
    }
    else if (key === '$or') {
        var orConditions = conditions[key];
        for (var _a = 0, orConditions_1 = orConditions; _a < orConditions_1.length; _a++) {
            var c = orConditions_1[_a];
            if (evaluate_conditional_logic(c, evaluate)) {
                return true;
            }
        }
        return false;
    }
    else if (key === 'condition') {
        var evalKey = Object.keys(conditions[key])[0];
        return evaluate(evalKey, 
        // @ts-ignore
        conditions[key][evalKey]);
    }
    return true;
};
export var age_for_dob_mmddyyyy = function (mmddyyyy) {
    var _a = mmddyyyy.split('-').map(function (s) { return parseInt(s); }), mm = _a[0], dd = _a[1], yyyy = _a[2]; // ensure second argument to parseInt is not provided
    if (isNaN(mm) || isNaN(dd) || isNaN(yyyy))
        return '';
    var monthIndexedByOne = new Date().getMonth() + 1;
    var ageForYear = new Date().getFullYear() - yyyy;
    var actualAge = (
    // dob is previous month, or dob is current month and day has passed
    (mm < monthIndexedByOne || (mm === monthIndexedByOne && dd <= new Date().getDate()))
        ? ageForYear
        : ageForYear - 1);
    return actualAge;
};
export var get_enduser_field_value_for_key = function (enduser, key) {
    var _a, _b;
    if (key === 'insurance.payerName')
        return (_a = enduser === null || enduser === void 0 ? void 0 : enduser.insurance) === null || _a === void 0 ? void 0 : _a.payerName;
    if (key === 'insuranceSecondary.payerName')
        return (_b = enduser === null || enduser === void 0 ? void 0 : enduser.insuranceSecondary) === null || _b === void 0 ? void 0 : _b.payerName;
    return enduser === null || enduser === void 0 ? void 0 : enduser[key];
};
export var evaluate_conditional_logic_for_enduser_fields = function (enduser, conditions) { return (evaluate_conditional_logic(conditions, function (key, value) {
    var _a, _b, _d, _e, _f;
    return ((key === 'Age' && typeof value === 'object')
        ? (function () {
            if (!enduser.dateOfBirth)
                return false;
            var age = age_for_dob_mmddyyyy(enduser.dateOfBirth);
            if (age === '')
                return false;
            var result = ((value === null || value === void 0 ? void 0 : value['$lt']) !== undefined
                ? (age < parseInt(value['$lt']))
                : (value === null || value === void 0 ? void 0 : value['$gt']) !== undefined
                    ? (age > parseInt(value['$gt']))
                    : false);
            return result;
        })()
        // : (key === 'Tags' && typeof value === 'object')
        //   ? (() => {
        //     if (!(value as ListOfStringsWithQualifier)?.values?.length) return true
        //     return (
        //       (value as ListOfStringsWithQualifier)?.qualifier === 'All Of'
        //         ? (
        //             !!enduser.tags?.length
        //           && enduser.tags?.filter(t => (value as ListOfStringsWithQualifier)?.values?.includes(t)).length === (value as ListOfStringsWithQualifier)?.values?.length
        //         )
        //         : !!enduser.tags?.find(t => (value as ListOfStringsWithQualifier)?.values?.includes(t))
        //     )
        //   })()
        : key === 'relationships' && typeof value === 'string'
            ? (!!((_a = enduser === null || enduser === void 0 ? void 0 : enduser.relationships) === null || _a === void 0 ? void 0 : _a.find(function (r) { return r.type === get_inverse_relationship_type(value); })))
            : typeof value === 'object'
                ? (function () {
                    var _a, _b, _d, _e, _f, _g, _h, _j, _k, _l;
                    var k = Object.keys(value)[0];
                    var v = Object.values(value)[0];
                    if (key === 'Journeys' && (k === '$in' || k === '$nin')) {
                        var isInJourney = ((_a = enduser === null || enduser === void 0 ? void 0 : enduser.journeys) === null || _a === void 0 ? void 0 : _a[v]) !== undefined;
                        return ((k === '$in' && isInJourney)
                            || (k === "$nin" && !isInJourney));
                    }
                    if (k === '$before' || k === '$after') {
                        var vDate = v === '$now' ? new Date() : new Date(v);
                        if (isNaN(vDate.getTime()))
                            return false;
                        var eDateField = (_d = (_b = enduser.fields) === null || _b === void 0 ? void 0 : _b[key]) !== null && _d !== void 0 ? _d : get_enduser_field_value_for_key(enduser, key);
                        if (!eDateField)
                            return false;
                        if (typeof eDateField !== 'string')
                            return false;
                        var eDate = ((eDateField.includes('-') && eDateField.length === 10)
                            ? new Date(MM_DD_YYYY_to_YYYY_MM_DD(eDateField))
                            : new Date(eDateField));
                        if (isNaN(eDate.getTime()))
                            return false;
                        return ((k === '$before' && eDate.getTime() < vDate.getTime())
                            || (k === '$after' && eDate.getTime() > vDate.getTime()));
                    }
                    if (k === '$lt' || k === '$gt') {
                        var enduserValue = (_e = enduser.fields) === null || _e === void 0 ? void 0 : _e[key];
                        if (typeof enduserValue !== 'number')
                            return false;
                        var _v = (typeof v === 'number'
                            ? v
                            : parseInt(v));
                        if (isNaN(_v))
                            return false;
                        return ((k === '$lt' && enduserValue < _v)
                            || (k === '$gt' && enduserValue > _v));
                    }
                    if (k === '$contains' || k === '$doesNotContain') {
                        var enduserValue = ((_g = (_f = enduser.fields) === null || _f === void 0 ? void 0 : _f[key]) !== null && _g !== void 0 ? _g : get_enduser_field_value_for_key(enduser, key));
                        var contains = (Array.isArray(enduserValue)
                            ? !!enduserValue.find(function (ev) { return typeof ev === 'string' && ev.includes(v); })
                            : typeof enduserValue === 'string'
                                ? enduserValue.includes(v)
                                : enduserValue === v);
                        return ((k === '$contains' && contains)
                            || (k === '$doesNotContain' && !contains));
                    }
                    if (k === '$isSet' || k === '$isNotSet') {
                        var enduserValue = ((_j = (_h = enduser.fields) === null || _h === void 0 ? void 0 : _h[key]) !== null && _j !== void 0 ? _j : get_enduser_field_value_for_key(enduser, key));
                        var isSet = (Array.isArray(enduserValue)
                            ? enduserValue.length > 0
                            : !!enduserValue);
                        return (k === "$isSet" && isSet) || (k === '$isNotSet' && !isSet);
                    }
                    // should negate the typeof value === 'string' (defaults to $equals) condition
                    if (k === '$ne') {
                        var enduserValue = ((_l = (_k = enduser.fields) === null || _k === void 0 ? void 0 : _k[key]) !== null && _l !== void 0 ? _l : get_enduser_field_value_for_key(enduser, key));
                        return !(enduserValue === v
                            || (Array.isArray(enduserValue) && (enduserValue).includes(v)));
                    }
                    return false;
                })()
                : typeof value === 'string'
                    ? (((_d = (_b = enduser.fields) === null || _b === void 0 ? void 0 : _b[key]) !== null && _d !== void 0 ? _d : get_enduser_field_value_for_key(enduser, key)) === value
                        || (Array.isArray((_e = enduser.fields) === null || _e === void 0 ? void 0 : _e[key])
                            && ((_f = enduser === null || enduser === void 0 ? void 0 : enduser.fields) === null || _f === void 0 ? void 0 : _f[key]).includes(value))
                        || (Array.isArray(get_enduser_field_value_for_key(enduser, key))
                            && enduser[key].includes(value)))
                    : false);
})); };
export var getLocalTimezone = function () { return Intl.DateTimeFormat().resolvedOptions().timeZone; };
export var YYYY_MM_DD_to_MM_DD_YYYY = function (yyyyMmDd, delimiter) {
    if (delimiter === void 0) { delimiter = '-'; }
    var _a = yyyyMmDd.split(delimiter), yyyy = _a[0], mm = _a[1], dd = _a[2];
    return "".concat(mm, "-").concat(dd, "-").concat(yyyy);
};
export var MM_DD_YYYY_to_YYYY_MM_DD = function (MMDDYYYY, delimiter) {
    if (delimiter === void 0) { delimiter = '-'; }
    var _a = MMDDYYYY.split(delimiter), mm = _a[0], dd = _a[1], yyyy = _a[2];
    return "".concat(yyyy, "-").concat(mm, "-").concat(dd);
};
export var get_recent_engagement_date = function (e) {
    var dates = [];
    if (e.recentEventBookedAt) {
        dates.push(new Date(e.recentEventBookedAt).getTime());
    }
    if (e.recentFormSubmittedAt) {
        dates.push(new Date(e.recentFormSubmittedAt).getTime());
    }
    if (e.recentInboundCallAt) {
        dates.push(new Date(e.recentInboundCallAt).getTime());
    }
    if (e.recentInboundChatAt) {
        dates.push(new Date(e.recentInboundChatAt).getTime());
    }
    if (e.recentInboundSMSAt) {
        dates.push(new Date(e.recentInboundSMSAt).getTime());
    }
    if (e.recentInboundEmailAt) {
        dates.push(new Date(e.recentInboundEmailAt).getTime());
    }
    var max = dates.sort().pop();
    return max ? new Date(max) : undefined;
};
export var get_recent_outbound_communication_date = function (e) {
    var dates = [];
    if (e.recentOutboundCallAt) {
        dates.push(new Date(e.recentOutboundCallAt).getTime());
    }
    if (e.recentOutboundChatAt) {
        dates.push(new Date(e.recentOutboundChatAt).getTime());
    }
    if (e.recentOutboundSMSAt) {
        dates.push(new Date(e.recentOutboundSMSAt).getTime());
    }
    if (e.recentOutboundEmailAt) {
        dates.push(new Date(e.recentOutboundEmailAt).getTime());
    }
    var max = dates.sort().pop();
    return max ? new Date(max) : undefined;
};
// https://www.w3schools.com/tags/ref_urlencode.ASP
// AWS provides = instead of % for some reason, so handle accoringly
var URIReplacements = {
    "=21": "!",
    "=22": "\"",
    "=23": "#",
    "=24": "$",
    "=25": "%",
    "=26": "&",
    "=27": "'",
    "=28": "(",
    "=29": ")",
    "=2A": "*",
    "=2B": "=+",
    "=2C": ",",
    "=2D": "-",
    "=2E": ".",
    "=2F": "/",
    "=30": "0",
    "=31": "1",
    "=32": "2",
    "=33": "3",
    "=34": "4",
    "=35": "5",
    "=36": "6",
    "=37": "7",
    "=38": "8",
    "=39": "9",
    "=3A": ":",
    "=3B": ";",
    "=3C": "<",
    "=3D": "=",
    "=3E": ">",
    "=3F": "?",
    "=40": "@",
    "=41": "A",
    "=42": "B",
    "=43": "C",
    "=44": "D",
    "=45": "E",
    "=46": "F",
    "=47": "G",
    "=48": "H",
    "=49": "I",
    "=4A": "J",
    "=4B": "K",
    "=4C": "L",
    "=4D": "M",
    "=4E": "N",
    "=4F": "O",
    "=50": "P",
    "=51": "Q",
    "=52": "R",
    "=53": "S",
    "=54": "T",
    "=55": "U",
    "=56": "V",
    "=57": "W",
    "=58": "X",
    "=59": "Y",
    "=5A": "Z",
    "=5B": "[",
    "=5C": "\\",
    "=5D": "]",
    "=5E": "^",
    "=5F": "_",
    "=60": "`",
    "=61": "a",
    "=62": "b",
    "=63": "c",
    "=64": "d",
    "=65": "e",
    "=66": "f",
    "=67": "g",
    "=68": "h",
    "=69": "i",
    "=6A": "j",
    "=6B": "k",
    "=6C": "l",
    "=6D": "m",
    "=6E": "n",
    "=6F": "o",
    "=70": "p",
    "=71": "q",
    "=72": "r",
    "=73": "s",
    "=74": "t",
    "=75": "u",
    "=76": "v",
    "=77": "w",
    "=78": "x",
    "=79": "y",
    "=7A": "z",
    "=7B": "{",
    "=7C": "|",
    "=7D": "}",
    "=7E": "~",
    "=7F": "",
    "=80": "",
    "=E2=82=AC": "",
    "=81": "",
    "=82": "",
    "=E2=80=9A": "",
    "=83": "",
    "=C6=92": "",
    "=84": "",
    "=E2=80=9E": "",
    "=85": "",
    "=E2=80=A6": "",
    "=86": "",
    "=E2=80=A0": "",
    "=87": "",
    "=E2=80=A1": "",
    "=88": "",
    "=CB=86": "",
    "=89": "",
    "=E2=80=B0": "",
    "=8A": "",
    "=C5=A0": "",
    "=8B": "",
    "=E2=80=B9": "",
    "=8C": "",
    "=C5=92": "",
    "=8D": "",
    "=C5=8D": "",
    "=8E": "Ž",
    "=C5=BD": "Ž",
    "=8F": "",
    "=90": "",
    "=C2=90": "",
    "=91": "‘",
    "=E2=80=98": "‘",
    "=92": "’",
    "=E2=80=99": "’",
    "=93": "“",
    "=E2=80=9C": "“",
    "=94": "”",
    "=E2=80=9D": "”",
    "=95": "•",
    "=E2=80=A2": "•",
    "=96": "–",
    "=E2=80=93": "–",
    "=97": "—",
    "=E2=80=94": "—",
    "=98": "˜",
    "=CB=9C": "˜",
    "=99": "™",
    "=E2=84": "™",
    "=9A": "",
    "=C5=A1": "",
    "=9B": "",
    "=E2=80": "",
    "=9C": "",
    "=C5=93": "",
    "=9D": "",
    "=9E": "",
    "=C5=BE": "",
    "=9F": "",
    "=C5=B8": "",
    "=A0": "",
    "=C2=A0": "",
    "=A1": "",
    "=C2=A1": "",
    "=A2": "",
    "=C2=A2": "",
    "=A3": "",
    "=C2=A3": "",
    "=A4": "",
    "=C2=A4": "",
    "=A5": "",
    "=C2=A5": "",
    "=A6": "",
    "=C2=A6": "",
    "=A7": "",
    "=C2=A7": "",
    "=A8": "",
    "=C2=A8": "",
    "=A9": "",
    "=C2=A9": "",
    "=AA": "",
    "=C2=AA": "",
    "=AB": "",
    "=C2=AB": "",
    "=AC": "",
    "=C2=AC": "",
    "=AD": "",
    "=C2=AD": "",
    "=AE": "",
    "=C2=AE": "",
    "=AF": "",
    "=C2=AF": "",
    "=B0": "",
    "=C2=B0": "",
    "=B1": "",
    "=C2=B1": "",
    "=B2": "",
    "=C2=B2": "",
    "=B3": "",
    "=C2=B3": "",
    "=B4": "",
    "=C2=B4": "",
    "=B5": "",
    "=C2=B5": "",
    "=B6": "",
    "=C2=B6": "",
    "=B7": "",
    "=C2=B7": "",
    "=B8": "",
    "=C2=B8": "",
    "=B9": "",
    "=C2=B9": "",
    "=BA": "",
    "=C2=BA": "",
    "=BB": "",
    "=C2=BB": "",
    "=BC": "",
    "=C2=BC": "",
    "=BD": "",
    "=C2=BD": "",
    "=BE": "",
    "=C2=BE": "",
    "=BF": "",
    "=C2=BF": "",
    "=C0": "",
    "=C3=80": "",
    "=C1": "",
    "=C3=81": "",
    "=C2": "",
    "=C3=82": "",
    "=C3": "",
    "=C3=83": "",
    "=C4": "",
    "=C3=84": "",
    "=C5": "",
    "=C3=85": "",
    "=C6": "",
    "=C3=86": "",
    "=C7": "",
    "=C3=87": "",
    "=C8": "",
    "=C3=88": "",
    "=C9": "",
    "=C3=89": "",
    "=CA": "",
    "=C3=8A": "",
    "=CB": "",
    "=C3=8B": "",
    "=CC": "",
    "=C3=8C": "",
    "=CD": "",
    "=C3=8D": "",
    "=CE": "",
    "=C3=8E": "",
    "=CF": "",
    "=C3=8F": "",
    "=D0": "",
    "=C3=90": "",
    "=D1": "",
    "=C3=91": "",
    "=D2": "",
    "=C3=92": "",
    "=D3": "",
    "=C3=93": "",
    "=D4": "",
    "=C3=94": "",
    "=D5": "",
    "=C3=95": "",
    "=D6": "",
    "=C3=96": "",
    "=D7": "",
    "=C3=97": "",
    "=D8": "",
    "=C3=98": "",
    "=D9": "",
    "=C3=99": "",
    "=DA": "",
    "=C3=9A": "",
    "=DB": "",
    "=C3=9B": "",
    "=DC": "",
    "=C3=9C": "",
    "=DD": "",
    "=C3=9D": "",
    "=DE": "",
    "=C3=9E": "",
    "=DF": "",
    "=C3=9F": "",
    "=E0": "",
    "=C3=A0": "",
    "=E1": "",
    "=C3=A1": "",
    "=E2": "",
    "=C3=A2": "",
    "=E3": "",
    "=C3=A3": "",
    "=E4": "",
    "=C3=A4": "",
    "=E5": "",
    "=C3=A5": "",
    "=E6": "",
    "=C3=A6": "",
    "=E7": "",
    "=C3=A7": "",
    "=E8": "",
    "=C3=A8": "",
    "=E9": "",
    "=C3=A9": "",
    "=EA": "",
    "=C3=AA": "",
    "=EB": "",
    "=C3=AB": "",
    "=EC": "",
    "=C3=AC": "",
    "=ED": "",
    "=C3=AD": "",
    "=EE": "",
    "=C3=AE": "",
    "=EF": "",
    "=C3=AF": "",
    "=F0": "",
    "=C3=B0": "",
    "=F1": "",
    "=C3=B1": "",
    "=F2": "",
    "=C3=B2": "",
    "=F3": "",
    "=C3=B3": "",
    "=F4": "",
    "=C3=B4": "",
    "=F5": "",
    "=C3=B5": "",
    "=F6": "",
    "=C3=B6": "",
    "=F7": "",
    "=C3=B7": "",
    "=F8": "",
    "=C3=B8": "",
    "=F9": "",
    "=C3=B9": "",
    "=FA": "",
    "=C3=BA": "",
    "=FB": "",
    "=C3=BB": "",
    "=FC": "",
    "=C3=BC": "",
    "=FD": "",
    "=C3=BD": "",
    "=FE": "",
    "=C3=BE": "",
    "=FF": "",
    "=C3=BF": "", // "ÿ"
};
export var URIDecodeEmail = function (content, verbose) { return (content
    .replace(/=\s/gi, '')
    .replaceAll("\r\n", '')
    .replace(/=21|=3D|=22|=23|=24|=25|=26|=27|=28|=29|=2A|=2B|=2C|=2D|=2E|=2F|=30|=31|=32|=33|=34|=35|=36|=37|=38|=39|=3A|=3B|=3C|=3E|=3F|=40|=41|=42|=43|=44|=45|=46|=47|=48|=49|=4A|=4B|=4C|=4D|=4E|=4F|=50|=51|=52|=53|=54|=55|=56|=57|=58|=59|=5A|=5B|=5C|=5D|=5E|=5F|=60|=61|=62|=63|=64|=65|=66|=67|=68|=69|=6A|=6B|=6C|=6D|=6E|=6F|=70|=71|=72|=73|=74|=75|=76|=77|=78|=79|=7A|=7B|=7C|=7D|=7E|=7F|=80|=E2=82=AC|=81|=82|=E2=80=9A|=83|=C6=92|=84|=E2=80=9E|=85|=E2=80=A6|=86|=E2=80=A0|=87|=E2=80=A1|=88|=CB=86|=89|=E2=80=B0|=8A|=C5=A0|=8B|=E2=80=B9|=8C|=C5=92|=8D|=C5=8D|=8E|=C5=BD|=8F|=90|=C2=90|=91|=E2=80=98|=92|=E2=80=99|=93|=E2=80=9C|=94|=E2=80=9D|=95|=E2=80=A2|=96|=E2=80=93|=97|=E2=80=94|=98|=CB=9C|=99|=E2=84|=9A|=C5=A1|=9B|=E2=80|=9C|=C5=93|=9D|=9E|=C5=BE|=9F|=C5=B8|=A0|=C2=A0|=A1|=C2=A1|=A2|=C2=A2|=A3|=C2=A3|=A4|=C2=A4|=A5|=C2=A5|=A6|=C2=A6|=A7|=C2=A7|=A8|=C2=A8|=A9|=C2=A9|=AA|=C2=AA|=AB|=C2=AB|=AC|=C2=AC|=AD|=C2=AD|=AE|=C2=AE|=AF|=C2=AF|=B0|=C2=B0|=B1|=C2=B1|=B2|=C2=B2|=B3|=C2=B3|=B4|=C2=B4|=B5|=C2=B5|=B6|=C2=B6|=B7|=C2=B7|=B8|=C2=B8|=B9|=C2=B9|=BA|=C2=BA|=BB|=C2=BB|=BC|=C2=BC|=BD|=C2=BD|=BE|=C2=BE|=BF|=C2=BF|=C0|=C3=80|=C1|=C3=81|=C2|=C3=82|=C3|=C3=83|=C4|=C3=84|=C5|=C3=85|=C6|=C3=86|=C7|=C3=87|=C8|=C3=88|=C9|=C3=89|=CA|=C3=8A|=CB|=C3=8B|=CC|=C3=8C|=CD|=C3=8D|=CE|=C3=8E|=CF|=C3=8F|=D0|=C3=90|=D1|=C3=91|=D2|=C3=92|=D3|=C3=93|=D4|=C3=94|=D5|=C3=95|=D6|=C3=96|=D7|=C3=97|=D8|=C3=98|=D9|=C3=99|=DA|=C3=9A|=DB|=C3=9B|=DC|=C3=9C|=DD|=C3=9D|=DE|=C3=9E|=DF|=C3=9F|=E0|=C3=A0|=E1|=C3=A1|=E2|=C3=A2|=E3|=C3=A3|=E4|=C3=A4|=E5|=C3=A5|=E6|=C3=A6|=E7|=C3=A7|=E8|=C3=A8|=E9|=C3=A9|=EA|=C3=AA|=EB|=C3=AB|=EC|=C3=AC|=ED|=C3=AD|=EE|=C3=AE|=EF|=C3=AF|=F0|=C3=B0|=F1|=C3=B1|=F2|=C3=B2|=F3|=C3=B3|=F4|=C3=B4|=F5|=C3=B5|=F6|=C3=B6|=F7|=C3=B7|=F8|=C3=B8|=F9|=C3=B9|=FA|=C3=BA|=FB|=C3=BB|=FC|=C3=BC|=FD|=C3=BD|=FE|=C3=BE|=FF|=C3=BF/gi, function (k) {
    // verbose 
    //   ? console.log(k, v[1]?.substring(v[0] - 8, v[0] + 8)) 
    //   : undefined,
    var _a;
    var v = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        v[_i - 1] = arguments[_i];
    }
    // const [i, s] = v
    // const match = s.substring(i, i + k.length) // match just equals k when used this way, but can -/+ to see longer string
    var toReturn = (_a = URIReplacements[k]) !== null && _a !== void 0 ? _a : k;
    // if (i > 3700 && i < 4000) {
    //   if (verbose) { console.log(s.substring(i - 25, i + k.length + 25).replace(k, '=')) }
    //   return match
    // }
    return toReturn;
})); };
export var mfa_is_enabled = function (u) {
    var _a;
    return (!!((_a = u === null || u === void 0 ? void 0 : u.mfa) === null || _a === void 0 ? void 0 : _a.email));
};
export var get_next_reminder_timestamp = function (_a) {
    var startTimeInMS = _a.startTimeInMS, reminders = _a.reminders;
    var pending = reminders === null || reminders === void 0 ? void 0 : reminders.filter(function (r) { return !r.didRemind; });
    if (!(pending === null || pending === void 0 ? void 0 : pending.length))
        return -1;
    var maxMsBeforeStartTime = Math.max.apply(Math, pending.map(function (p) { return p.msBeforeStartTime; }));
    return startTimeInMS - maxMsBeforeStartTime;
};
export var capture_is_supported = function () {
    try {
        return document.createElement('input').capture !== undefined;
    }
    catch (err) {
        console.error(err);
        return false;
    }
};
export var batch_array = function (array, size) {
    var batches = [];
    for (var i = 0; i < Math.ceil(array.length / size); i++) {
        var batch = [];
        for (var j = i * size; j < (i + 1) * size; j++) {
            if (j >= array.length)
                break;
            batch.push(array[j]);
        }
        batches.push(batch);
    }
    return batches;
};
export var calculate_form_scoring = function (_a) {
    var _b;
    var response = _a.response, form = _a.form;
    if (!((_b = form.scoring) === null || _b === void 0 ? void 0 : _b.length))
        return;
    var scores = {};
    var _loop_1 = function (scoreCondition) {
        var r = response.responses.find(function (r) { return r.fieldId === scoreCondition.fieldId
            && (r.answer.type !== 'multiple_choice'
                || (Array.isArray(r.answer.value)
                    && typeof scoreCondition.response === 'string'
                    && r.answer.value.includes(scoreCondition.response))); });
        if (!r)
            return "continue";
        if (scores[scoreCondition.title] === undefined) {
            scores[scoreCondition.title] = 0;
        }
        if (r.answer.type === 'multiple_choice' && typeof scoreCondition.score === 'number') {
            scores[scoreCondition.title] += scoreCondition.score;
        }
        else if (typeof r.answer.value === 'number') {
            scores[scoreCondition.title] += r.answer.value;
        }
    };
    for (var _i = 0, _d = form.scoring; _i < _d.length; _i++) {
        var scoreCondition = _d[_i];
        _loop_1(scoreCondition);
    }
    if (object_is_empty(scores))
        return;
    var scoresList = [];
    for (var title in scores) {
        scoresList.push({
            title: title,
            value: scores[title]
        });
    }
    return scoresList;
};
// don't change order without updating responses_satisfy_conditions calculations
export var FORM_LOGIC_CALCULATED_FIELDS = ['Calculated: BMI', 'Calculated: Age', 'Calculated: Score', 'Gender'];
export var FORM_LOGIC_URL_PARAMETER = 'URL Logic Parameter';
export var calculate_bmi = function (e) {
    var _a, _b, _d, _e;
    var height = typeof ((_a = e.height) === null || _a === void 0 ? void 0 : _a.value) === 'number' ? e.height.value : parseInt(((_b = e.height) === null || _b === void 0 ? void 0 : _b.value) || '0');
    var weight = typeof ((_d = e.weight) === null || _d === void 0 ? void 0 : _d.value) === 'number' ? e.weight.value : parseInt(((_e = e.weight) === null || _e === void 0 ? void 0 : _e.value) || '0');
    if (!height)
        return -1;
    if (!weight)
        return -1;
    return (703 * weight / (height * height));
};
// keep consistent with convert_form_logic_to_filter logic in analytics.ts
export var responses_satisfy_conditions = function (responses, conditions, options) {
    var _a, _b, _d, _e, _f, _g, _h;
    var key = Object.keys(conditions)[0]; // string is form id
    if (key === '$and') {
        var andConditions = conditions[key];
        for (var _i = 0, andConditions_2 = andConditions; _i < andConditions_2.length; _i++) {
            var c = andConditions_2[_i];
            if (!responses_satisfy_conditions(responses, c, options)) {
                return false;
            }
        }
        return true;
    }
    else if (key === '$or') {
        var orConditions = conditions[key];
        for (var _j = 0, orConditions_2 = orConditions; _j < orConditions_2.length; _j++) {
            var c = orConditions_2[_j];
            if (responses_satisfy_conditions(responses, c, options)) {
                return true;
            }
        }
        return false;
    }
    else if (key === 'condition') {
        var fieldIdOrCalculated_1 = Object.keys(conditions[key])[0];
        var answer = (fieldIdOrCalculated_1 === FORM_LOGIC_CALCULATED_FIELDS[0] // bmi
            ? (function () {
                var _a, _b, _d, _e;
                var h = (((_a = responses.find(function (r) { return r.answer.type === 'number' && r.answer.value && r.computedValueKey === 'Height'; })) === null || _a === void 0 ? void 0 : _a.answer)
                    || ((_b = responses.find(function (r) { return r.answer.type === 'Height' && r.answer.value && r.computedValueKey === 'Height'; })) === null || _b === void 0 ? void 0 : _b.answer));
                var w = (_d = responses.find(function (r) { return r.answer.type === 'number' && r.answer.value && r.computedValueKey === 'Weight'; })) === null || _d === void 0 ? void 0 : _d.answer;
                var height = (((h === null || h === void 0 ? void 0 : h.type) === 'number' && h.value)
                    ? h.value
                    : ((h === null || h === void 0 ? void 0 : h.type) === 'Height' && typeof ((_e = h.value) === null || _e === void 0 ? void 0 : _e.feet) === 'number')
                        ? h.value.feet * 12 + (h.value.inches || 0)
                        : undefined);
                var weight = (w === null || w === void 0 ? void 0 : w.type) === 'number' && w.value ? w === null || w === void 0 ? void 0 : w.value : undefined;
                if (!(height && weight))
                    return undefined;
                var BMI = {
                    type: 'number',
                    value: 703 * weight / (height * height)
                };
                return BMI;
            })()
            : fieldIdOrCalculated_1 === FORM_LOGIC_CALCULATED_FIELDS[1] // age
                ? (function () {
                    var _a, _b, _d;
                    var dob = (options === null || options === void 0 ? void 0 : options.dateOfBirth) || ((_d = (_b = (_a = responses.find(function (r) { return r.answer.type === 'dateString' && r.answer.value && r.computedValueKey === 'Date of Birth'; })) === null || _a === void 0 ? void 0 : _a.answer) === null || _b === void 0 ? void 0 : _b.value) === null || _d === void 0 ? void 0 : _d.toString());
                    if (!dob)
                        return undefined;
                    try {
                        var age = age_for_dob_mmddyyyy(dob);
                        if (typeof age !== 'number')
                            return undefined;
                        var Age = {
                            type: 'number',
                            value: age
                        };
                        return Age;
                    }
                    catch (err) {
                        console.error(err);
                    }
                })()
                : fieldIdOrCalculated_1 === FORM_LOGIC_CALCULATED_FIELDS[2] // score
                    ? (function () {
                        var form = options === null || options === void 0 ? void 0 : options.form;
                        if (!form)
                            return undefined;
                        var scores = calculate_form_scoring({ response: { responses: options.activeResponses || responses }, form: form });
                        if (!(scores === null || scores === void 0 ? void 0 : scores.length))
                            return;
                        var Score = {
                            type: 'number',
                            value: scores[0].value,
                        };
                        return Score;
                    })()
                    : fieldIdOrCalculated_1 === FORM_LOGIC_CALCULATED_FIELDS[3] // gender
                        ? (function () {
                            var _a, _b, _d;
                            var gender = (((_d = // responses should be priority, in case they're being used to change gender
                            (_b = (_a = responses.find(function (r) { return (r.answer.type === 'Dropdown' || r.answer.type === 'multiple_choice') && r.computedValueKey === 'Gender'; })) === null || _a === void 0 ? void 0 : _a.answer) === null || _b === void 0 ? void 0 : _b.value) === null || _d === void 0 ? void 0 : _d[0])
                                || (options === null || options === void 0 ? void 0 : options.gender));
                            var Gender = {
                                type: 'string',
                                value: gender || '',
                            };
                            return Gender;
                        })()
                        : fieldIdOrCalculated_1 === FORM_LOGIC_URL_PARAMETER
                            ? { type: 'string', value: (options === null || options === void 0 ? void 0 : options.urlLogicValue) || '' }
                            : (_a = responses.find(function (r) { return r.fieldId === fieldIdOrCalculated_1; })) === null || _a === void 0 ? void 0 : _a.answer);
        if (!answer)
            return false;
        var comparison_1 = conditions[key][fieldIdOrCalculated_1];
        if (typeof comparison_1 === 'string') {
            if (answer.type === 'Database Select' && ((_b = answer.value) === null || _b === void 0 ? void 0 : _b.length)) {
                return (!!answer.value.find(function (v) { return (v === null || v === void 0 ? void 0 : v.text) === comparison_1; }));
            }
            if (answer.type === 'Insurance') {
                return ((_d = answer.value) === null || _d === void 0 ? void 0 : _d.payerName) === comparison_1;
            }
            if (answer.type === 'Address') {
                return (comparison_1 === ((_e = answer.value) === null || _e === void 0 ? void 0 : _e.state)
                    || comparison_1 === ((_f = answer.value) === null || _f === void 0 ? void 0 : _f.zipCode)
                    || comparison_1 === ((_g = answer.value) === null || _g === void 0 ? void 0 : _g.city));
            }
            return (Array.isArray(answer.value)
                ? answer.value.includes(comparison_1)
                : answer.value === comparison_1);
        }
        else {
            var condition = Object.keys(comparison_1)[0];
            var conditionValue_1 = comparison_1[condition];
            if (condition === '$lt' || condition === '$gt') {
                if (conditionValue_1 === '$now') {
                    if (answer.type === 'date' && answer.value) {
                        var number_1 = Date.now();
                        var answerNumber_1 = new Date(answer.value).getTime();
                        if (condition === '$lt') {
                            return answerNumber_1 < number_1;
                        }
                        if (condition === '$gt') {
                            return answerNumber_1 > number_1;
                        }
                    }
                    if (answer.type === 'dateString' && answer.value) {
                        var number_2 = new Date(MM_DD_YYYY_to_YYYY_MM_DD(mm_dd_yyyy(new Date()))).getTime();
                        var answerNumber_2 = new Date(MM_DD_YYYY_to_YYYY_MM_DD(answer.value)).getTime();
                        if (condition === '$lt') {
                            return answerNumber_2 < number_2;
                        }
                        if (condition === '$gt') {
                            return answerNumber_2 > number_2;
                        }
                    }
                }
                var number = parseInt(conditionValue_1);
                var answerNumber = answer.value;
                if (isNaN(number))
                    return false;
                if (typeof answerNumber !== 'number')
                    return false;
                if (condition === '$lt') {
                    return answerNumber < number;
                }
                if (condition === '$gt') {
                    return answerNumber > number;
                }
            }
            else if (condition === '$exists') {
                return (answer.value !== undefined
                    && answer.value !== null
                    && answer.value !== ''
                    && !(Array.isArray(answer.value) && answer.value.length === 0)) === conditionValue_1;
            }
            else if (condition === '$contains' || condition === '$doesNotContain') {
                if (answer.value === undefined || answer.value === null || answer.value === '') {
                    return condition === '$doesNotContain'; // empty responses cannot contain anything
                }
                if (answer.type === 'Database Select' && ((_h = answer.value) === null || _h === void 0 ? void 0 : _h.length)) {
                    var contains = !!answer.value.find(function (v) { var _a; return (_a = v.text) === null || _a === void 0 ? void 0 : _a.includes(conditionValue_1); });
                    return ((contains && condition === '$contains')
                        || (!contains && condition === '$doesNotContain'));
                }
                if (Array.isArray(answer.value)) {
                    var contains = answer.value.find(function (v) { return typeof v === 'string' && v.includes(conditionValue_1); });
                    return ((contains && condition === '$contains')
                        || (!contains && condition === '$doesNotContain'));
                }
                if (typeof answer.value === 'string') {
                    var contains = answer.value.includes(conditionValue_1);
                    return ((contains && condition === '$contains')
                        || (!contains && condition === '$doesNotContain'));
                }
            }
        }
    }
    return true;
};
export var weighted_round_robin = function (_a) {
    var _assignments = _a.assignments, _users = _a.users;
    if (!_users.length) {
        return { selected: undefined };
    }
    if (_users.length === 1) {
        return { selected: _users[0].id };
    }
    // ensure default value set (5) with max of 10
    var users = _users.map(function (u) { return ({
        id: u.id,
        ticketAssignmentPriority: Math.min(u.ticketAssignmentPriority || 5, 10),
    }); });
    var windowSize = users.reduce(function (t, u) { return t + u.ticketAssignmentPriority; }, 0);
    // descending, with newest timestamp at [0], oldest timestamp at [assignments.length - 1]
    var assignments = (__spreadArray([], _assignments, true).sort(function (a1, a2) { return a2.timestamp - a1.timestamp; })
        .filter(function (a, i) { return users.find(function (u) { return u.id === a.userId; }) && i < windowSize - 1; }) // pigeonhole to ensure 1 available user
    );
    var capacityForUser = {};
    for (var _i = 0, users_1 = users; _i < users_1.length; _i++) {
        var user = users_1[_i];
        capacityForUser[user.id] = user.ticketAssignmentPriority;
    }
    for (var _b = 0, assignments_1 = assignments; _b < assignments_1.length; _b++) {
        var a = assignments_1[_b];
        if (!capacityForUser[a.userId])
            continue;
        capacityForUser[a.userId] -= 1;
    }
    // find user with capacity who has gone the longest without a ticket assignment
    var delayScores = {};
    var _loop_2 = function (i) {
        var delayIndex = assignments.findIndex(function (a) { return a.userId === users[i].id; });
        delayScores[users[i].id] = delayIndex === -1 ? assignments.length : delayIndex;
    };
    for (var i = 0; i < users.length; i++) {
        _loop_2(i);
    }
    var usersSorted = __spreadArray([], users, true).sort(function (u1, u2) { return delayScores[u2.id] - delayScores[u1.id]; });
    for (var _d = 0, usersSorted_1 = usersSorted; _d < usersSorted_1.length; _d++) {
        var user = usersSorted_1[_d];
        if (capacityForUser[user.id] !== undefined && capacityForUser[user.id] > 0) {
            return { selected: user.id };
        }
    }
    // for (let i = assignments.length -1; i >= 0; i--) {
    //   const a = assignments[i]
    //   const user = users.find(u => u.id === a.userId)
    //   if (!user) { continue }
    //   if (capacityForUser[user.id] !== undefined && capacityForUser[user.id] > 0) { 
    //     return { selected: user.id }
    //   }
    //   // return { selected: user.id }
    // }
    return { selected: undefined };
};
export var validate_insurance_for_eligibility = function (enduser) {
    var _a, _b, _d;
    if (!enduser.insurance) {
        return "Insurance not set";
    }
    if (!enduser.insurance.payerName) {
        return "Payer name not set";
    }
    if (!enduser.insurance.payerId) {
        return "Payer id not set";
    }
    if (!enduser.insurance.memberId) {
        return "Member id not set";
    }
    if (!enduser.insurance.relationship) {
        return "Subscriber relationship not set";
    }
    if (enduser.insurance.relationship !== 'Self') {
        if (!((_a = enduser.insurance.relationshipDetails) === null || _a === void 0 ? void 0 : _a.fname)) {
            return "Subscriber first name not set";
        }
        if (!((_b = enduser.insurance.relationshipDetails) === null || _b === void 0 ? void 0 : _b.lname)) {
            return "Subscriber last name not set";
        }
        if (!((_d = enduser.insurance.relationshipDetails) === null || _d === void 0 ? void 0 : _d.gender)) {
            return "Subscriber gender not set";
        }
    }
};
export var validate_organization_for_candid = function (organization) {
    var _a, _b, _d, _e, _f;
    if (!organization)
        return "Organization is required";
    if (!organization.billingOrganizationName)
        return "Billing organization name is required";
    if (!organization.billingOrganizationNPI)
        return "Billing organization NPI is required";
    if (!organization.billingOrganizationTaxId)
        return "Billing organization Tax ID is required";
    if (!((_a = organization.billingOrganizationAddress) === null || _a === void 0 ? void 0 : _a.lineOne))
        return "Billing organization address is required (Line One)";
    if (!((_b = organization.billingOrganizationAddress) === null || _b === void 0 ? void 0 : _b.city))
        return "Billing organization address is required (City)";
    if (!((_d = organization.billingOrganizationAddress) === null || _d === void 0 ? void 0 : _d.state))
        return "Billing organization address is required (State)";
    if (!((_e = organization.billingOrganizationAddress) === null || _e === void 0 ? void 0 : _e.zipCode))
        return "Billing organization address is required (ZIP)";
    if (!((_f = organization.billingOrganizationAddress) === null || _f === void 0 ? void 0 : _f.zipPlusFour))
        return "Billing organization address is required (ZIP+4)";
};
export var validate_provider_for_candid = function (user) {
    if (!user)
        return "User is required";
    if (!user.fname)
        return "User first name is required";
    if (!user.lname)
        return "User last name is required";
    if (!user.NPI)
        return "User NPI is required";
};
export var validate_enduser_for_candid = function (enduser) {
    if (!enduser)
        return "Enduser is required";
    if (!enduser.fname)
        return "First name is required";
    if (!enduser.lname)
        return "Last name is required";
    if (!enduser.dateOfBirth)
        return "Date of birth is required";
    if (!enduser.gender)
        return "Gender is required";
    if (!enduser.addressLineOne)
        return "Address is required (Line One)";
    if (!enduser.city)
        return "Address is required (City)";
    if (!enduser.state)
        return "Address is required (State)";
    if (!enduser.zipCode)
        return "Address is required (ZIP)";
};
export var json_error_string = function (s) {
    try {
        return JSON.stringify(JSON.parse(s), null, 2);
    }
    catch (err) {
        return s;
    }
};
export var validate_enduser_for_gogo = function (enduser) {
    if (!enduser)
        return "Enduser is required";
    if (!enduser.fname)
        return "First name is required";
    if (!enduser.lname)
        return "Last name is required";
    if (!enduser.phone)
        return "Phone is required";
    if (!enduser.addressLineOne)
        return "Address is required (Line One)";
    if (!enduser.city)
        return "Address is required (City)";
    if (!enduser.state)
        return "Address is required (State)";
    if (!enduser.zipCode)
        return "Address is required (ZIP)";
};
export var validate_enduser_for_smart_meter = function (enduser) {
    if (!enduser)
        return "Enduser is required";
    if (!enduser.fname)
        return "First name is required";
    if (!enduser.lname)
        return "Last name is required";
    if (!enduser.addressLineOne)
        return "Address is required (Line One)";
    if (!enduser.city)
        return "Address is required (City)";
    if (!enduser.state)
        return "Address is required (State)";
    if (!enduser.zipCode)
        return "Address is required (ZIP)";
};
export var validate_enduser_for_dose_spot = function (enduser) {
    var _a, _b;
    if (!enduser)
        return "Enduser is required";
    if (!enduser.fname)
        return "First name is required";
    if (!enduser.lname)
        return "Last name is required";
    if (!enduser.gender)
        return "Gender is required";
    if (!enduser.dateOfBirth)
        return "Date of Birth is required";
    if (!enduser.addressLineOne)
        return "Address is required (Line One)";
    if (!enduser.city)
        return "Address is required (City)";
    if (!enduser.state)
        return "Address is required (State)";
    if (!enduser.zipCode)
        return "Address is required (ZIP)";
    if (!enduser.phone)
        return "Phone is required";
    var age = age_for_dob_mmddyyyy(enduser.dateOfBirth);
    if (typeof age === 'number' && age < 19) {
        if (!((_a = enduser.height) === null || _a === void 0 ? void 0 : _a.value))
            return "Height is required for patients under 19";
        if (!((_b = enduser.weight) === null || _b === void 0 ? void 0 : _b.value))
            return "Weight is required for patients under 19";
    }
};
// https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
export var decodeJWT = function (jwt) {
    try {
        var base64Url = jwt.split('.')[1];
        var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    }
    catch (err) {
        return null;
    }
};
export var field_can_autoadvance = function (_a) {
    var type = _a.type, options = _a.options;
    if (type === 'multiple_choice' && (options === null || options === void 0 ? void 0 : options.radio))
        return true;
    if (type === 'Dropdown' && (options === null || options === void 0 ? void 0 : options.radio))
        return true;
    if (type === 'Appointment Booking')
        return true;
    if (type === 'Stripe')
        return true;
    return false;
};
export var field_can_autosubmit = function (_a) {
    var type = _a.type, options = _a.options;
    if (type === 'Appointment Booking')
        return true;
    if (type === 'Stripe')
        return true;
    return false;
};
export var satisfies_vital_comparison = function (comparison, value) {
    if (comparison.type === 'Less Than') {
        return value < comparison.value;
    }
    if (comparison.type === 'Greater Than') {
        return value > comparison.value;
    }
    if (comparison.type === 'Between') {
        return value >= comparison.value.lower && value <= comparison.value.upper;
    }
    return false;
};
export var SMS_UNSUBSCRIBE_KEYWORDS = ['stop', 'stopall', 'unsubscribe', 'end', 'quit', 'cancel'];
export var classification_for_vital = function (v, configurations) {
    var _a, _b, _d;
    var classification = (((_a = v.classifications) === null || _a === void 0 ? void 0 : _a.length) === 1 ? v.classifications[0].classification
        : ((_d = (_b = v.classifications) === null || _b === void 0 ? void 0 : _b.find(function (c) { return configurations.find(function (_c) { return _c.id === c.configurationId; }); })) === null || _d === void 0 ? void 0 : _d.classification));
    return classification || '';
};
// ,'High','Low','Very High','Very Low','Critical High','Critical Low'
export var color_for_classification = function (c, ifNoMatch) { return (c === 'Target' ? '#44cc4466'
    : c === 'Low' ? '#cccc4466'
        : c === 'High' ? '#cccc4466'
            : c === 'Very High' ? '#cc444466'
                : c === 'Very Low' ? '#cc444466'
                    : c === 'Critical High' ? 'violet'
                        : c === 'Critical Low' ? 'violet'
                            : ifNoMatch); };
export var is_out_of_office = function (blocks, date, zone) {
    var _a, _b;
    if (date === void 0) { date = new Date(); }
    if (zone === void 0) { zone = 'America/New_York'; }
    if (blocks.length === 0)
        return false;
    var nowInTimezone = DateTime.fromJSDate(date, { zone: zone });
    var nowMinutes = nowInTimezone.minute + 60 * nowInTimezone.hour;
    var nowDay = ( // convert to zero indexing by Sunday
    nowInTimezone.weekday === 7 // Get the day of the week. 1 is Monday and 7 is Sunday
        ? 0
        : nowInTimezone.weekday);
    for (var _i = 0, blocks_1 = blocks; _i < blocks_1.length; _i++) {
        var block = blocks_1[_i];
        if (((_a = block.active) === null || _a === void 0 ? void 0 : _a.to) && (new Date(block.active.to).getTime() < Date.now()))
            continue; // ends before now
        if (((_b = block.active) === null || _b === void 0 ? void 0 : _b.from) && (new Date(block.active.from).getTime() > Date.now()))
            continue; // starts after now
        if (nowDay !== block.dayOfWeekStartingSundayIndexedByZero)
            continue; // different day
        // same day, and within the availability window
        if (nowMinutes > block.startTimeInMinutes && nowMinutes < block.endTimeInMinutes) {
            return false;
        }
    }
    return true;
};
export var get_utm_params = function () {
    var params = new URL(window.location.href).searchParams;
    var utmParams = [];
    params.forEach(function (value, field) {
        if (field.toLowerCase().startsWith('utm_')) {
            utmParams.push({ field: field, value: value });
        }
    });
    return utmParams;
};
export var append_current_utm_params = function (targetURL) {
    try {
        var params = new URL(window.location.href).searchParams;
        var utmParams_1 = {};
        params.forEach(function (value, key) {
            if (key.toLowerCase().startsWith('utm_')) {
                utmParams_1[key] = value;
            }
        });
        if (object_is_empty(utmParams_1)) {
            return targetURL;
        }
        var modifiedURL = targetURL;
        if (!modifiedURL.includes('?')) {
            modifiedURL += '?';
        }
        else if (!modifiedURL.endsWith('&')) {
            modifiedURL += '&';
        }
        for (var param in utmParams_1) {
            modifiedURL += "".concat(param, "=").concat(utmParams_1[param], "&");
        }
        return modifiedURL;
    }
    catch (err) {
        console.error(err);
    }
    ;
    return targetURL;
};
export var replace_tag_template_values_for_enduser = function (tags, enduser) { return (tags.map(function (t) {
    var _a, _b, _d;
    if (t.startsWith('{{') && t.endsWith('}}')) {
        var tagField = (t.split('{{enduser.').pop() || '').replace("}}", '');
        if (tagField === 'hashedPassword')
            return t;
        return (((_b = (_a = enduser.fields) === null || _a === void 0 ? void 0 : _a[tagField]) === null || _b === void 0 ? void 0 : _b.toString())
            || ((_d = get_enduser_field_value_for_key(enduser, tagField)) === null || _d === void 0 ? void 0 : _d.toString()) // accounts for dotted fields like insurance.payerName
            || t);
    }
    return t;
})); };
export var replace_enduser_template_values = function (s, enduser) {
    var i = 0;
    var start = 0;
    var templates = [];
    while (i < 100) {
        i++;
        start = s.indexOf('{{enduser.', start);
        if (start === -1)
            break;
        var end = s.indexOf('}}', start);
        if (end === -1)
            break;
        var match = s.substring(start, end + 2); // +2 accounts for '}}' 
        templates.push({
            match: match,
            replacement: replace_tag_template_values_for_enduser([match], enduser)[0],
        });
        start = end + 2;
    }
    var replaced = s.toString();
    for (var _i = 0, templates_1 = templates; _i < templates_1.length; _i++) {
        var _a = templates_1[_i], match = _a.match, replacement = _a.replacement;
        replaced = replaced.replace(match, replacement);
    }
    return replaced;
};
export var display_time_for_seconds = function (seconds) { return (!seconds
    ? ''
    : seconds > 60
        ? " (".concat(Math.floor(seconds / 60), " Minutes)")
        : " (".concat(seconds, " Seconds)")); };
export var is_full_iso_string_heuristic = function (d) {
    try {
        if (d.length < 12)
            return; // don't consider simple YYYY-MM-DD (10 characters) dates
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
            return;
        }
        var asDate = new Date(d);
        return !isNaN(asDate.getTime()) ? asDate : undefined;
    }
    catch (err) {
        return;
    }
};
export var skip_due_date_as_needed = function (toSkip) { return function (dueDateInMS) {
    if (!dueDateInMS)
        return dueDateInMS;
    if (!(toSkip === null || toSkip === void 0 ? void 0 : toSkip.length))
        return dueDateInMS;
    var adjusted = dueDateInMS;
    for (var i = 0; i < 7; i++) { // limit to 7 iterations
        if (toSkip.includes(new Date(adjusted).getDay())) {
            adjusted += 1000 * 60 * 60 * 24;
        }
        else {
            break;
        }
    }
    return adjusted;
}; };
export var get_flattened_fields = function (objects, options) {
    var _a = options !== null && options !== void 0 ? options : {}, _b = _a.ignore, ignore = _b === void 0 ? [] : _b, _d = _a.maxDepth, maxDepth = _d === void 0 ? 5 : _d, _e = _a.depth, depth = _e === void 0 ? 0 : _e, includeObjectRoot = _a.includeObjectRoot;
    var fields = [];
    if (depth > maxDepth) {
        return fields;
    }
    for (var _i = 0, objects_1 = objects; _i < objects_1.length; _i++) {
        var o = objects_1[_i];
        if (!o || typeof o !== 'object' || Array.isArray(o))
            continue;
        for (var rootField in o) {
            if (ignore.includes(rootField))
                continue;
            var value = o[rootField];
            if (!value || typeof value !== 'object' || Array.isArray(value)) {
                fields.push(rootField);
                continue;
            }
            var field = rootField;
            // captures root field of object fields
            if (includeObjectRoot) {
                if (!fields.includes(field)) {
                    fields.push(field);
                }
            }
            // don't include root on nested fields
            var nestedFields = get_flattened_fields([value], __assign(__assign({}, options), { includeObjectRoot: false, ignore: [], depth: depth + 1 }));
            for (var _f = 0, nestedFields_1 = nestedFields; _f < nestedFields_1.length; _f++) {
                var nestedField = nestedFields_1[_f];
                var toAdd = "".concat(field, ".").concat(nestedField);
                if (fields.includes(toAdd))
                    continue;
                fields.push(toAdd);
            }
        }
    }
    return fields;
};
export var value_for_dotted_key = function (v, key) {
    var value = v;
    var keys = key.split('.');
    for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
        var k = keys_1[_i];
        value = value === null || value === void 0 ? void 0 : value[k];
    }
    return value;
};
export var add_value_for_dotted_key = function (_object, field, value) {
    var keys = field.split('.');
    var object = _object;
    for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        if (i === keys.length - 1) {
            object[key] = value;
            continue;
        }
        object = object[key];
    }
};
export var INVALID_PREPOPULATION_TYPES = [
    'file',
    'files',
    'description',
    'Question Group',
    'Stripe', // can't prepopulate this
    // now supported
    // "Address", // split into different patient fields and non-trivial to pre-load  
];
export var get_prepopulated_responses = function (fields, enduser) { return (fields
    .filter(function (v) {
    var _a;
    return (v.prepopulateFromFields && !INVALID_PREPOPULATION_TYPES.includes(v.type) && v.intakeField
        && ((v.intakeField === 'Address' &&
            (enduser.addressLineOne || enduser.addressLineTwo || enduser.zipCode || enduser.city || enduser.zipCode || enduser.state))
            || (v.intakeField === 'insurance.details' && enduser.insurance)
            || (v.intakeField === 'insuranceSecondary.details' && enduser.insuranceSecondary)
            || ((enduser === null || enduser === void 0 ? void 0 : enduser[v.intakeField]) || ((_a = enduser === null || enduser === void 0 ? void 0 : enduser.fields) === null || _a === void 0 ? void 0 : _a[v.intakeField]))));
})
    .map(function (v) {
    var _a, _b, _d, _e, _f, _g, _h, _j;
    return ({
        fieldId: v.id,
        fieldTitle: v.title,
        externalId: v.externalId,
        fieldDescription: v.description,
        fieldHtmlDescription: v.htmlDescription,
        sharedWithEnduser: v.sharedWithEnduser,
        answer: (v.type === 'Address' && v.intakeField === 'Address')
            ? {
                type: 'Address',
                value: {
                    addressLineOne: enduser.addressLineOne || '',
                    addressLineTwo: enduser.addressLineTwo || '',
                    city: enduser.city || '',
                    state: enduser.state || '',
                    zipCode: enduser.zipCode || '',
                    zipPlusFour: enduser.zipPlusFour || '',
                }
            }
            : (v.type === 'Insurance' && v.intakeField === 'insurance.details')
                ? {
                    type: 'Insurance',
                    value: enduser.insurance,
                }
                : (v.type === 'Insurance' && v.intakeField === 'insuranceSecondary.details')
                    ? {
                        type: 'Insurance',
                        value: enduser.insuranceSecondary,
                    }
                    : (v.type === 'Database Select' && typeof ((enduser === null || enduser === void 0 ? void 0 : enduser[v.intakeField]) || ((_a = enduser === null || enduser === void 0 ? void 0 : enduser.fields) === null || _a === void 0 ? void 0 : _a[v.intakeField])) === 'string')
                        ? {
                            type: "Database Select",
                            value: [((enduser === null || enduser === void 0 ? void 0 : enduser[v.intakeField]) || ((_b = enduser === null || enduser === void 0 ? void 0 : enduser.fields) === null || _b === void 0 ? void 0 : _b[v.intakeField]))]
                        }
                        : (v.type === 'Height' && ((_d = enduser.height) === null || _d === void 0 ? void 0 : _d.value))
                            ? {
                                type: 'Height',
                                value: {
                                    feet: Math.floor((parseInt(enduser.height.value) / 12)),
                                    inches: Math.floor((parseInt(enduser.height.value) % 12)),
                                }
                            }
                            : (v.type === 'number' && v.intakeField === 'weight' && ((_e = enduser === null || enduser === void 0 ? void 0 : enduser.weight) === null || _e === void 0 ? void 0 : _e.value))
                                ? { type: 'number', value: parseInt((_f = enduser === null || enduser === void 0 ? void 0 : enduser.weight) === null || _f === void 0 ? void 0 : _f.value) }
                                : (v.type === 'number' && v.intakeField === 'height' && ((_g = enduser === null || enduser === void 0 ? void 0 : enduser.height) === null || _g === void 0 ? void 0 : _g.value))
                                    ? { type: 'number', value: parseInt((_h = enduser === null || enduser === void 0 ? void 0 : enduser.height) === null || _h === void 0 ? void 0 : _h.value) }
                                    : {
                                        type: v.type,
                                        value: ((enduser === null || enduser === void 0 ? void 0 : enduser[v.intakeField]) || ((_j = enduser === null || enduser === void 0 ? void 0 : enduser.fields) === null || _j === void 0 ? void 0 : _j[v.intakeField]))
                                    }
    });
})); };
export var downloadFile = function (data, options) {
    var name = options.name, type = options.type;
    name = name || "download.txt";
    type = type || "octet/stream";
    var a = document.createElement("a");
    document.body.appendChild(a);
    a.style.display = "none";
    var blob = new Blob([data], { type: type });
    var url = ((options.dataIsURL && typeof data === 'string')
        ? data
        : window.URL.createObjectURL(blob));
    a.href = url;
    a.download = name;
    a.click();
    window.URL.revokeObjectURL(url);
};
export var is_timezone = function (value) { return (typeof value === 'string' && TIMEZONES.includes(value)); };
export var enduser_address_string = function (e) { return ("".concat(e.addressLineOne ? e.addressLineOne : '').concat(e.addressLineTwo ? " ".concat(e.addressLineTwo) : '').concat(e.addressLineOne || e.addressLineTwo ? ', ' : '').concat(e.city ? "".concat(e.city, ", ") : '').concat(e.state ? "".concat(e.state).concat(e.zipCode ? ', ' : '') : '').concat(e.zipCode ? e.zipCode : '').concat(e.zipPlusFour && e.zipCode ? "-".concat(e.zipPlusFour) : '')); };
export var enduser_insurance_string = function (i) { return !i ? '' : ("".concat(i.payerName).concat(i.memberId ? " (".concat(i.memberId, ")") : '')); };
//# sourceMappingURL=utils.js.map